<template>
  <div ref="rootRef" class="language-selector">
    <button
      type="button"
      class="language-trigger"
      :class="`language-trigger--${variant}${variant === 'footer' ? ' core-action-button core-action-button--supporting' : ''}`"
      aria-haspopup="menu"
      :aria-expanded="isMenuOpen ? 'true' : 'false'"
      :title="`Language (${activeLabel})`"
      @click="toggleMenu">
      <IconWorld v-if="variant === 'header'" class="h-[18px] w-[18px]" stroke="1.6" />
      <IconWorld v-else class="h-5 w-5" stroke="2.1" />
      <span class="language-code">{{ activeLabel }}</span>
      <!-- The header shows the name from md on and the code on narrower screens -->
      <span v-if="variant === 'header'" class="language-short">{{ activeCode }}</span>
    </button>

    <div
      v-if="isMenuOpen"
      class="language-menu"
      :class="`language-menu--${variant}`"
      role="menu"
      aria-label="Language selector">
      <button
        v-for="option in options"
        :key="option.locale"
        type="button"
        class="language-menu-item"
        :class="{ active: option.locale === active }"
        :aria-pressed="option.locale === active"
        @click="select(option.locale)">
        <span>{{ option.label }}</span>
        <IconCheck v-if="option.locale === active" class="h-[1.1rem] w-[1.1rem]" stroke="2" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { IconCheck, IconWorld } from '@tabler/icons-vue'

/** Language button with its menu; the caller decides which languages exist and what a choice does. */
const props = withDefaults(
  defineProps<{
    languages: readonly string[]
    active: string
    /**
     * `footer`: the app's button with language codes, menu opens upwards.
     * `header`: a plain text button with the language names, menu opens downwards.
     */
    variant?: 'footer' | 'header'
  }>(),
  { variant: 'footer' }
)

const emit = defineEmits<{ (e: 'select', language: string): void }>()

const isMenuOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)

// Each language in its own language, so people who cannot read the current one still find theirs
const NATIVE_NAMES: Record<string, string> = { de: 'Deutsch', fr: 'Français', it: 'Italiano', en: 'English' }

const label = (locale: string) => {
  const base = locale.split('-')[0]
  return props.variant === 'header' && NATIVE_NAMES[base] ? NATIVE_NAMES[base] : base.toUpperCase()
}

const options = computed(() => props.languages.map((locale) => ({ locale, label: label(locale) })))
const activeLabel = computed(() => label(props.active) || 'DE')
const activeCode = computed(() => props.active.split('-')[0].toUpperCase() || 'DE')

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const select = (language: string) => {
  closeMenu()
  if (language !== props.active) emit('select', language)
}

const onDocumentClick = (event: MouseEvent) => {
  const target = event.target as Node | null
  if (!target || !rootRef.value?.contains(target)) {
    closeMenu()
  }
}

const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onDocumentKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onDocumentKeydown)
})
</script>

<style scoped lang="postcss">
.language-selector {
  @apply relative inline-flex;
}

.language-trigger--footer {
  @apply min-w-[4.5rem] gap-2 rounded-none pl-4 pr-5 py-3 text-base text-neutral-550 font-semibold;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default focus-visible:ring-offset-1;
}

.language-trigger--footer .language-code {
  @apply text-base font-semibold;
}

.language-trigger:hover {
  @apply text-primary-dark;
}

/* Text button that matches the header text; the padding only enlarges the click area */
.language-trigger--header {
  @apply -mr-2 inline-flex items-center gap-[7px] px-2 py-1.5 text-neutral-650;
  @apply transition-colors duration-150;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default;

  .language-code,
  .language-short {
    @apply font-normal;
    font-size: 15px;
  }

  .language-code {
    @apply hidden md:inline;
  }

  .language-short {
    @apply md:hidden;
  }
}

.language-menu {
  @apply absolute z-[220] min-w-[6rem] border border-neutral-300 bg-white px-1 py-1 shadow-lg;
  animation: language-menu-fade 100ms ease-out;
}

.language-menu--footer {
  @apply bottom-full left-1/2 mb-2;
  transform: translateX(-50%);
}

.language-menu--header {
  @apply right-0 top-full mt-1;
}

.language-menu-item {
  @apply flex w-full items-center justify-between gap-2 border-0 bg-transparent px-3 py-1.5 text-left text-sm font-semibold tracking-wide text-neutral-700;
  @apply focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-default focus-visible:ring-inset;
}

.language-menu-item:hover {
  background-color: #f5f5f5;
}

.language-menu-item.active {
  @apply border text-primary-dark;
  background-color: color-mix(in srgb, var(--color-primary-default) 6%, white);
  border-color: color-mix(in srgb, var(--color-primary-default) 48%, white);
}

@keyframes language-menu-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
